import { fetchApi, fetchRest, getApiBaseUrl } from '@/config/api';

export interface CouleurAPILike {
  id_couleur: number;
  couleur_FR: string;
  couleur_EN: string | null;
  hexColor?: string | null;
  officiel?: number | null;
  /** NULL = couleur unie, non NULL = motif texture (Animaux, Carreaux…) */
  motifCategorie?: string | null;
  idCouleurEfashion?: number | null;
  familleEfashion?: string | null;
}

// Interface pour les couleurs défaut
interface CouleurDefaut {
  id_couleur: number;
  couleur_FR: string;
  couleur_EN: string;
  hex?: string | null;
  officiel?: number;
  motif_categorie?: string | null;
  id_couleur_efashion?: number | null;
  famille_efashion?: string | null;
}

export interface AdminCouleurDefaut {
  id_couleur: number;
  couleur_FR: string;
  couleur_EN: string | null;
  hex: string | null;
  officiel: number;
  defaut: number;
  id_couleur_efashion: number | null;
  motif_categorie: string | null;
  famille_efashion?: string | null;
}

export interface AdminCouleurFamille {
  id_couleur_efashion: number;
  libelle: string;
}

export interface AdminCouleurInput {
  couleur_FR: string;
  couleur_EN?: string | null;
  hex?: string | null;
  officiel?: number | boolean;
  id_couleur_efashion?: number | null;
  motif_categorie?: string | null;
  allow_duplicate?: boolean;
}

async function readAdminError(res: Response, fallback: string): Promise<string> {
  const payload = await res.json().catch(() => null);
  if (typeof payload?.message === 'string') return payload.message;
  if (Array.isArray(payload?.message)) return payload.message.join(', ');
  return fallback;
}

/** Catalogue couleurs (officielles + non officielles). */
export async function getCouleurs(): Promise<CouleurAPILike[]> {
  const query = `
    query GetCouleurs {
      couleurs {
        id_couleur
        couleur_FR
        couleur_EN
        hex
        officiel
        motif_categorie
        id_couleur_efashion
      }
    }
  `;

  const response = await fetchApi(query);
  const rows: CouleurDefaut[] = response.couleurs || [];
  return rows.map((c) => ({
    id_couleur: Number(c.id_couleur),
    couleur_FR: c.couleur_FR,
    couleur_EN: c.couleur_EN ?? null,
    hexColor: c.hex || null,
    officiel: c.officiel ?? 0,
    motifCategorie: c.motif_categorie ?? null,
    idCouleurEfashion:
      c.id_couleur_efashion == null ? null : Number(c.id_couleur_efashion),
    familleEfashion: null,
  }));
}

/** Catalogue officiel (MEL, import Excel) — couleurs_defaut où officiel = 1 */
export async function getCouleursOfficielles(): Promise<CouleurAPILike[]> {
  const query = `
    query GetCouleursOfficielles {
      couleursOfficielles {
        id_couleur
        couleur_FR
        couleur_EN
        hex
        officiel
        motif_categorie
        id_couleur_efashion
      }
    }
  `;

  const response = await fetchApi(query);
  const rows: CouleurDefaut[] = response.couleursOfficielles || [];
  return rows.map((c) => ({
    id_couleur: Number(c.id_couleur),
    couleur_FR: c.couleur_FR,
    couleur_EN: c.couleur_EN ?? null,
    hexColor: c.hex || null,
    officiel: c.officiel ?? 1,
    motifCategorie: c.motif_categorie ?? null,
    idCouleurEfashion:
      c.id_couleur_efashion == null ? null : Number(c.id_couleur_efashion),
    familleEfashion: null,
  }));
}

/** Liste admin (toutes, ou officielles si officielOnly). */
export async function adminListCouleurs(
  officielOnly = false,
): Promise<AdminCouleurDefaut[]> {
  const qs = officielOnly ? '?officiel=1' : '';
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/couleurs${qs}`,
  );
  if (!res.ok) {
    throw new Error(await readAdminError(res, 'Impossible de charger les couleurs'));
  }
  return res.json();
}

export async function adminListCouleurFamilles(): Promise<AdminCouleurFamille[]> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/couleurs?meta=familles`,
  );
  if (!res.ok) {
    throw new Error(await readAdminError(res, 'Impossible de charger les familles'));
  }
  const payload = await res.json();
  if (!Array.isArray(payload)) return [];
  return payload
    .map((row: Record<string, unknown>) => ({
      id_couleur_efashion: Number(row.id_couleur_efashion),
      libelle: String(row.libelle ?? row.couleur_FR ?? '').trim(),
    }))
    .filter(
      (row) =>
        Number.isFinite(row.id_couleur_efashion) &&
        row.id_couleur_efashion > 0 &&
        row.libelle.length > 0,
    );
}

export async function adminCreateCouleur(
  input: AdminCouleurInput,
): Promise<AdminCouleurDefaut> {
  const res = await fetchRest(`${getApiBaseUrl()}/api/admin-efp/couleurs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(await readAdminError(res, 'Impossible de créer la couleur'));
  }
  return res.json();
}

export async function adminUpdateCouleur(
  id: number,
  input: AdminCouleurInput,
): Promise<AdminCouleurDefaut> {
  const res = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/couleurs/${id}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    throw new Error(
      await readAdminError(res, 'Impossible de mettre à jour la couleur'),
    );
  }
  return res.json();
}
